import { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { PRIVATE_ROUTES } from '../../constants/privateRoutes.constants';
import { URIS_AS_TITLES } from '../../constants/uriAsTitle.constants';
import { AuthContext } from '../../context/AuthContext';
import type { UserSliceState } from '../../interfaces/user.interface';
import type { UserAvatarState } from '../../interfaces/userPreferences.interface';
import { getAvatarUrl } from '../../services/getPresignedUrlREST';
import {
  SET_LOADING_AVATAR,
  SET_USER_AVATAR_URL,
} from '../../store/userAvatar.store';
import { GlobalHeader } from '../GlobalHeader';
import './header.scss';

interface UserState {
  userStore: UserSliceState;
}

const Header = () => {
  const dispatch = useDispatch();
  // TRIL-141/TRIL-156: identity-check guard reads the canonical root user.avatar at commit time so a stale presign can't win the last write after the key changed.
  const store = useStore() as {
    getState: () => { userStore: { user: { avatar?: string | null } } };
  };
  const { userAvatarUrl } = useSelector(
    (state: UserAvatarState) => state.userAvatarStore,
  );
  const { user } = useSelector((state: UserState) => state.userStore);
  const location = useLocation();
  const isUser = location.pathname.includes('/user/');
  const isDashboardRoute = location.pathname.includes('/dashboard');
  const [uri, setUri] = useState<string | undefined>(undefined);
  const [headerUserName, setHeaderUsername] = useState<string>('');
  const [userAvatarKey, setUserAvatarKey] = useState<string | null>(null);
  const authContext = useContext(AuthContext);
  const { isSignedIn, session } = authContext;

  useEffect(() => {
    // TRIL-156: avatar key is canonical at root user.avatar.
    setUserAvatarKey((user.avatar as string) ?? null);
  }, [user.avatar]);

  // TRIL-141/TRIL-156: identity-check after the await against root user.avatar (the canonical key source). Skipping the URL commit on a mismatch closes the stale-write window. SET_LOADING_AVATAR(false) always fires in finally so the loading flag can't stick.
  const getUrl = useCallback(async () => {
    if (!user.avatar) return;
    const capturedKey = user.avatar;
    // TRIL-306: prefer the server-presigned URL hydrated onto the user
    // (userPreferences.avatarUrl, set by UserHydrationGate) over the client
    // getAvatarUrl presign endpoint, which is unprovisioned in some envs
    // (VITE_API_GET_USER_AVATAR unset → ERR_NAME_NOT_RESOLVED). The presign
    // stays as a last-resort fallback for the key-only state.
    const serverUrl = user.userPreferences?.avatarUrl;
    if (serverUrl) {
      dispatch(SET_USER_AVATAR_URL(serverUrl));
      return;
    }
    dispatch(SET_LOADING_AVATAR(true));
    try {
      const res = await getAvatarUrl(capturedKey);
      if (store.getState().userStore.user.avatar !== capturedKey) return;
      dispatch(SET_USER_AVATAR_URL(res));
    } catch (error) {
      if (store.getState().userStore.user.avatar !== capturedKey) return;
      console.error('Error fetching avatar URL:', error);
    } finally {
      dispatch(SET_LOADING_AVATAR(false));
    }
  }, [user.avatar, user.userPreferences?.avatarUrl, dispatch, store]);

  useEffect(() => {
    if (userAvatarKey !== null && !userAvatarUrl) {
      getUrl().catch((e) => console.error('Error in avatar URL effect:', e));
    }
  }, [getUrl, userAvatarKey, userAvatarUrl]);

  useEffect(() => {
    // TRIL-306: only re-sync a truthy URL. Dispatching an empty/null url here
    // would clobber a fresh value written by getUrl in the same effect flush
    // (the server-URL preference) and loop. Never overwrite a real URL with
    // empty — on logout the root reducer clears the slice anyway.
    if (userAvatarKey !== null && userAvatarUrl) {
      dispatch(SET_USER_AVATAR_URL(userAvatarUrl as string));
    }
  }, [userAvatarKey, userAvatarUrl, dispatch]);

  const extractTitleFromPathname = useCallback(() => {
    for (const route of PRIVATE_ROUTES) {
      if (location.pathname.includes(route.uri)) {
        const titleKey = route.name
          .toUpperCase()
          .replace(/ /g, '_') as keyof typeof URIS_AS_TITLES;
        if (titleKey in URIS_AS_TITLES) {
          return setUri(URIS_AS_TITLES[titleKey]);
        }
      }
    }
  }, [location.pathname]);

  const formatUsername = useCallback((username: string) => {
    if (username.slice(-1) === 's') {
      setHeaderUsername(`${username}'`);
    } else {
      setHeaderUsername(`${username}'s`);
    }
  }, []);

  useEffect(() => {
    formatUsername(user.username);
  }, [user.username, formatUsername]);

  useEffect(() => {
    extractTitleFromPathname();
  }, [extractTitleFromPathname]);

  return (
    <section className="APP__HEADER__SECTION">
      <header
        className={`APP__HEADER ${isUser && isDashboardRoute ? 'APP__HEADER__Dashboard' : 'APP__HEADER__Feed'}`}
      >
        {/* TRIL-140: gate on isSignedIn (synchronous via AuthContext's
            localStorage rehydration), not user.username (which lives in
            Redux and stays '' until GET_USER_QUERY resolves async). The
            old gate broke nav rendering on hard reload + new-tab — the
            user is authenticated, but Redux is still hydrating, so the
            entire GlobalHeader (including NavigationRail) was hidden. */}
        {isSignedIn && (
          <GlobalHeader
            newUser={user.newUser as boolean}
            firstName={user.firstName as string}
            lastName={user.lastName as string}
            routeName={user.username as string}
            username={user.username as string}
          />
        )}
      </header>
    </section>
  );
};

export default Header;
